import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Tabs,
  NavLink,
  Icon,
  LoadingSpinner,
} from '@allied-solutions/affinity'
import styled, { css } from 'styled-components'
import { DashboardTileLoanInformation } from './DashboardTileLoanInformation'
import { DashboardTileCoverageRequirements } from './DashboardTileCoverageRequirements'
import { DashboardTileVerificationStatus } from './DashboardTileVerificationStatus'
import {
  Synced,
  Shield02,
  Bank,
} from '@allied-solutions/affinity-react-icons/standard'

export const DashboardTileContentMobile = ({
  borrowerData,
  coverageRequirementsData,
  isLoadingCoverageRequirements,
  coverageRequirementsError,
  isCoverageRequirementsError,
  onFetchCoverageRequirements,
  unmetRequirementsCount,
  openUploadModal,
  onActiveCoverageReferenceIdChange,
  onOpenHelp,
}) => {
  const [activeTab, setActiveTab] = useState('verification-status')
  const [
    coverageRequirementsAccountSlug,
    setCoverageRequirementsAccountSlug,
  ] = useState('0')

  const handleActiveCoverageAccountChange = (accountIndex, referenceId) => {
    setCoverageRequirementsAccountSlug(String(accountIndex))
    if (referenceId != null && referenceId !== '') {
      onActiveCoverageReferenceIdChange?.(String(referenceId))
    }
  }

  const navigateToCoverageRequirementsForAccount = accountIndex => {
    const account = coverageRequirementsData?.[accountIndex]
    handleActiveCoverageAccountChange(accountIndex, account?.ReferenceID)
    setActiveTab('coverage-requirements')
    onFetchCoverageRequirements?.()
  }

  const handleRequiredClick = () => {
    const account = coverageRequirementsData?.[0]
    handleActiveCoverageAccountChange(0, account?.ReferenceID)
    setActiveTab('coverage-requirements')
    onFetchCoverageRequirements?.()
  }

  const StyledNav = styled(Tabs.Nav)`
    display: flex;
    justify-content: space-between;
    gap: ${props => props.theme.space['md']};
    border-bottom: 1px solid;
    border-color: ${props => props.theme.colors.border.defaultLight};
    padding-bottom: ${props => props.theme.space['4xl']};
  `

  const StyledNavItem = styled(Tabs.NavItem)`
    margin: 0;
    flex: 1;

    ${NavLink} {
      padding: ${props => `${props.theme.space['2xl']}`};
      border-radius: ${props => props.theme.radii[1]};
      color: ${props => props.theme.colors.text.secondary};
      font-size: 16px;
      line-height: 20px;
      background-color: transparent;
      display: flex;
      flex: 1;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      gap: ${props => props.theme.space['md']};

      &:after {
        display: none;
      }
    }
    ${Icon} {
      color: ${props => props.theme.colors.icons.gray};
    }

    ${props =>
      props.active &&
      css`
        ${NavLink} {
          background-color: ${props => props.theme.colors.background.grayMuted};
          color: ${props => props.theme.colors.text.brand};

          & ${Icon} {
            color: ${props => props.theme.colors.icons.brand};
          }
        }
      `}
  `

  const StyledTabsContent = styled(Tabs.Content)`
    padding: ${props => props.theme.space['4xl']} 0;
  `

  const IconWithBadgeWrapper = styled.span`
    position: relative;
    display: inline-flex;
  `

  const ErrorBadge = styled.span`
    position: absolute;
    top: -4px;
    right: -4px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${props => props.theme.colors?.error};
    border: 2px solid white;
    box-sizing: border-box;
  `

  return (
    <Box>
      <Tabs idPrefix="Dashboard__Tile__Mobile__Navigation__Tabs">
        {() => (
          <>
            <StyledNav>
              {/* refactor: use typeStyle on these items instead of in styled component */}
              <StyledNavItem
                active={activeTab === 'verification-status'}
                slug="verification-status"
                onClick={() => setActiveTab('verification-status')}
              >
                <Icon src={Synced} />
                Status
              </StyledNavItem>
              <StyledNavItem
                active={activeTab === 'coverage-requirements'}
                onClick={handleRequiredClick}
                slug="coverage-requirements"
              >
                <IconWithBadgeWrapper>
                  <Icon src={Shield02} />
                  {unmetRequirementsCount > 0 && <ErrorBadge />}
                </IconWithBadgeWrapper>
                Required
              </StyledNavItem>
              <StyledNavItem
                active={activeTab === 'loan-information'}
                slug="loan-information"
                onClick={() => setActiveTab('loan-information')}
              >
                <Icon src={Bank} />
                Loan
              </StyledNavItem>
            </StyledNav>
            <StyledTabsContent>
              {activeTab === 'verification-status' && (
                <>
                  {isLoadingCoverageRequirements && (
                    <Box display="flex" alignItems="center" gap="md" py="lg">
                      <LoadingSpinner size="sm" />
                    </Box>
                  )}
                  {!isLoadingCoverageRequirements &&
                    coverageRequirementsData && (
                      <DashboardTileVerificationStatus
                        coverageRequirementsData={coverageRequirementsData}
                        onNavigateToCoverageRequirements={
                          navigateToCoverageRequirementsForAccount
                        }
                        onActiveCoverageAccountChange={
                          handleActiveCoverageAccountChange
                        }
                      />
                    )}
                </>
              )}
              {activeTab === 'coverage-requirements' && (
                <Box>
                  {isLoadingCoverageRequirements && (
                    <Box display="flex" alignItems="center" gap="md" py="lg">
                      <LoadingSpinner size="sm" />
                    </Box>
                  )}
                  {!isLoadingCoverageRequirements &&
                    coverageRequirementsData && (
                      <DashboardTileCoverageRequirements
                        coverageRequirementsData={coverageRequirementsData}
                        initialAccountSlug={coverageRequirementsAccountSlug}
                        unmetRequirementsCount={unmetRequirementsCount}
                        openUploadModal={openUploadModal}
                        onActiveCoverageAccountChange={
                          handleActiveCoverageAccountChange
                        }
                        onOpenHelp={onOpenHelp}
                      />
                    )}
                </Box>
              )}
              {activeTab === 'loan-information' && (
                <DashboardTileLoanInformation borrowerData={borrowerData} />
              )}
            </StyledTabsContent>
          </>
        )}
      </Tabs>
    </Box>
  )
}

DashboardTileContentMobile.propTypes = {
  borrowerData: PropTypes.object,
  coverageRequirementsData: PropTypes.arrayOf(PropTypes.object),
  isLoadingCoverageRequirements: PropTypes.bool,
  coverageRequirementsError: PropTypes.object,
  isCoverageRequirementsError: PropTypes.bool,
  onFetchCoverageRequirements: PropTypes.func,
  unmetRequirementsCount: PropTypes.number,
  openUploadModal: PropTypes.func,
  onActiveCoverageReferenceIdChange: PropTypes.func,
  onOpenHelp: PropTypes.func,
}
